import { formatAccessibleTime } from 'helpers/accessibility/formatAccessibleTime';
import useTranslation from 'next-translate/useTranslation';
import { ArrowIcon } from 'public/svg/IconsSvg';
import { useState } from 'react';
import { twJoin } from 'tailwind-merge';
import { OpeningHours } from 'types/openingHours';

export type OpeningHoursDayProps = {
    status: 'open' | 'closed' | 'default';
};

export type OpeningHoursTableTypeProps = {
    type?: 'small';
};

type OpeningHoursTableProps = OpeningHours &
    OpeningHoursTableTypeProps & { isCollapsed?: boolean; shortDayNames?: boolean };

const OpeningHoursTable: FC<OpeningHoursTableProps> = (props) => {
    const testIdentifier = 'block-openinghours';
    const [isCollapsed, setIsCollapsed] = useState<boolean>(props.isCollapsed ?? false);
    const { t, lang } = useTranslation();

    const dayNames = [
        t('Monday'),
        t('Tuesday'),
        t('Wednesday'),
        t('Thursday'),
        t('Friday'),
        t('Saturday'),
        t('Sunday'),
    ];

    const formatDayTime = (time: string) => time.replace(/^0(?=[1-9])/, '');
    const formatDayName = (day: string) => (props.shortDayNames ? day.substring(0, 2) : day);

    const getOpeningStatusOfDayByIndex = (index: number): OpeningHoursDayProps => {
        const { isCurrentlyOpen, currentDayOfTheWeek } = props;
        if (currentDayOfTheWeek === index) {
            return isCurrentlyOpen ? { status: 'open' } : { status: 'closed' };
        }
        const day = props.days[index];

        return day.intervals.length === 0 ? { status: 'closed' } : { status: 'default' };
    };

    return (
        <div
            className={twJoin('mb-4 break-words tracking-wide', props.type !== 'small' && 'mt-8 w-full')}
            data-testid={testIdentifier}
        >
            <table className="table-auto border-collapse">
                <tbody>
                    {props.days
                        .slice(0, isCollapsed ? 2 : props.days.length)
                        .map(({ dayOfTheWeek, intervals }, rowIndex) => {
                            const isClosedWholeDay = intervals.length === 0;
                            const isToday = dayOfTheWeek === props.currentDayOfTheWeek;
                            const statusProps = getOpeningStatusOfDayByIndex(rowIndex);

                            const ariaClosedText = `${formatDayName(dayNames[dayOfTheWeek])}, ${t('Closed')}`;
                            const ariaOpenText = `${formatDayName(dayNames[dayOfTheWeek])}, ${t('Open')} ${intervals
                                .map(({ opening, closing }) => {
                                    const openingFormatted = formatAccessibleTime(opening, lang);
                                    const closingFormatted = formatAccessibleTime(closing, lang);

                                    return `${openingFormatted} ${t('to')} ${closingFormatted}`;
                                })
                                .join(', ')}`;

                            const dayAriaText = isClosedWholeDay ? ariaClosedText : ariaOpenText;

                            return (
                                <tr
                                    key={rowIndex}
                                    aria-current={isToday ? 'date' : undefined}
                                    aria-label={dayAriaText}
                                    className="align-top"
                                    role="group"
                                    tabIndex={0}
                                >
                                    <th
                                        aria-hidden="true"
                                        scope="row"
                                        className={twJoin(
                                            'whitespace-nowrap py-1 pr-4 text-left font-semibold',
                                            props.type !== 'small' ? 'text-small' : 'text-tiny',
                                            statusProps.status === 'open' && 'text-greenContrast',
                                            statusProps.status === 'closed' && 'text-red',
                                            statusProps.status === 'default' && 'text-primary',
                                        )}
                                    >
                                        {formatDayName(dayNames[dayOfTheWeek])}
                                    </th>
                                    <td
                                        aria-hidden="true"
                                        className={twJoin(
                                            'flex w-full flex-row gap-x-3 py-1 font-normal',
                                            props.type !== 'small' ? 'text-small' : 'text-tiny',
                                            statusProps.status === 'open' && 'text-greenContrast',
                                            statusProps.status === 'closed' && 'text-red',
                                            statusProps.status === 'default' && 'text-primary',
                                        )}
                                    >
                                        {intervals.length > 0
                                            ? intervals.map(({ opening, closing }, columnIndex) => (
                                                  <div key={columnIndex}>
                                                      {`${formatDayTime(opening)} – ${formatDayTime(closing)}`}
                                                  </div>
                                              ))
                                            : t('Closed')}
                                    </td>
                                </tr>
                            );
                        })}
                    {isCollapsed && (
                        <tr>
                            <td className="pt-2" />
                            <td className="pt-2">
                                <button
                                    className={twJoin(
                                        'cursor-pointer border-none bg-transparent p-0 text-tiny outline-none',
                                        'hover:text-grayDark',
                                        '[&>svg]:ml-1 [&>svg]:w-3',
                                        '[&>span]:underline',
                                    )}
                                    onClick={() => setIsCollapsed(false)}
                                >
                                    <span>{t('Show whole week')}</span> <ArrowIcon className="w-3" />
                                </button>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default OpeningHoursTable;
